type KnownDeployment = {
  branch: string;
  canonicalEnvironment: string;
  domain: string;
  vercelProject: string;
};

const KNOWN_DEPLOYMENTS: Record<string, KnownDeployment> = {
  dev: {
    branch: 'dev',
    canonicalEnvironment: 'development',
    domain: 'dev.imai.studio',
    vercelProject: 'imai-dev',
  },
  prd: {
    branch: 'prd',
    canonicalEnvironment: 'production',
    domain: 'imai.studio',
    vercelProject: 'imai-prd',
  },
  stg: {
    branch: 'main',
    canonicalEnvironment: 'beta',
    domain: 'beta.imai.studio',
    vercelProject: 'imai-stg',
  },
};

const ENVIRONMENT_ALIASES: Record<string, keyof typeof KNOWN_DEPLOYMENTS> = {
  beta: 'stg',
  development: 'dev',
  prod: 'prd',
  production: 'prd',
  staging: 'stg',
};

export type PostHogDeploymentProperties = {
  convex_deployment: string;
  deployment_branch: string;
  deployment_commit: string;
  deployment_domain: string;
  deployment_environment: string;
  env: string;
  environment: string;
  vercel_project: string;
};

type ResolvePostHogDeploymentPropertiesArgs = {
  convexUrl?: string;
  hostname?: string;
  imaiEnvironment?: string;
  vercelGitCommitRef?: string;
  vercelGitCommitSha?: string;
  vercelTargetEnvironment?: string;
};

function normalize(value?: string): string {
  return value?.trim().toLowerCase() ?? '';
}

function normalizeHostname(value?: string): string {
  const normalized = normalize(value);
  if (!normalized) return '';

  try {
    return new URL(
      normalized.includes('://') ? normalized : `https://${normalized}`
    ).hostname;
  } catch {
    return normalized;
  }
}

function deploymentNameFromUrl(url?: string): string {
  if (!url) return 'unknown';

  try {
    return new URL(url).hostname.split('.')[0] || 'unknown';
  } catch {
    return 'unknown';
  }
}

export function resolvePostHogDeploymentProperties({
  convexUrl,
  hostname,
  imaiEnvironment,
  vercelGitCommitRef,
  vercelGitCommitSha,
  vercelTargetEnvironment,
}: ResolvePostHogDeploymentPropertiesArgs): PostHogDeploymentProperties {
  const requestedEnvironment =
    normalize(imaiEnvironment) ||
    normalize(vercelTargetEnvironment) ||
    'unknown';
  const knownEnvironment =
    ENVIRONMENT_ALIASES[requestedEnvironment] ?? requestedEnvironment;
  const knownDeployment = KNOWN_DEPLOYMENTS[knownEnvironment];
  const isPreview = requestedEnvironment === 'preview';

  return {
    convex_deployment: deploymentNameFromUrl(convexUrl),
    deployment_branch:
      normalize(vercelGitCommitRef) ||
      knownDeployment?.branch ||
      (isPreview ? 'preview' : 'unknown'),
    deployment_commit: vercelGitCommitSha?.trim() || 'unknown',
    deployment_domain:
      normalizeHostname(hostname) || knownDeployment?.domain || 'unknown',
    deployment_environment:
      knownDeployment?.canonicalEnvironment ||
      (isPreview ? 'preview' : requestedEnvironment),
    env: requestedEnvironment,
    environment: requestedEnvironment,
    vercel_project:
      knownDeployment?.vercelProject || (isPreview ? 'imai-dev' : 'unknown'),
  };
}
